export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

/**
 * Deeply trim all string values in an object/array structure.
 * Safeguards:
 * - Detects and preserves circular references (returns original reference to avoid infinite recursion)
 * - Only traverses plain objects (skips class instances, Firestore refs, Dates, etc.)
 * - Preserves Date instances as-is
 */
export function deepTrim<T>(input: T, seen: WeakSet<object> = new WeakSet()): T {
  // Primitive string
  if (typeof input === 'string') return input.trim() as unknown as T

  // Arrays
  if (Array.isArray(input)) {
    if (seen.has(input)) return input // circular
    seen.add(input)
    return input.map(v => deepTrim(v as unknown, seen)) as unknown as T
  }

  // Plain objects only
  if (isPlainObject(input)) {
    const obj = input as unknown as Record<string, unknown>
    if (seen.has(obj)) return input // circular
    seen.add(obj)

    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj)) {
      // Preserve special values (Dates, null, undefined)
      if (v instanceof Date || v === null || v === undefined) {
        out[k] = v
        continue
      }
      // Skip known relational back-references that can cause deep graphs
      // We only need to trim user-entered string fields, not entire related entity graphs.
      if (k === 'subkrewe' || k === 'members' || k === 'captains') {
        out[k] = v
        continue
      }
      out[k] = deepTrim(v as unknown, seen)
    }
    return out as T
  }

  // Anything else (number, boolean, Date, function, class instances, Firestore refs, etc.)
  return input
}
